import {
  AddBusinessOutlined,
  AttachFileOutlined,
  FolderOpenOutlined,
  GridViewOutlined,
  HomeWorkOutlined,
  StoreOutlined,
  ViewAgendaOutlined,
  WaterOutlined,
} from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import { isNil, size } from "lodash";
import { memo, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import useLocalStorageState from "use-local-storage-state";
import Flx from "../../../../components/layout/Flx";
import Htag from "../../../../components/typography/Htag";
import Txt from "../../../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { isConstructionProduct } from "../../../../utils/loan/productType";
import { sidebarTypeToggle } from "../../../../redux/actions/sidebarActions";
import {
  DETAIL_SIDEBAR_CONTENT_TYPES,
  detailSidebarOpen,
} from "../../../../redux/reducers/detailSidebarReducer";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import { formatNumber } from "../../../../utils/numbers/formatNumber";
import { nsKey } from "../../../../utils/storage/partnerStorage";
import LoanDocumentManagerTable from "../LoanDrilldownDocumentsTab/LoanDocumentManagerTable";
import PropertyCard from "./components/PropertyCard";
import { useLoanMarketData } from "./components/PropertyCardSections";
import PropertyCardCompact from "./components/PropertyCardCompact";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import KpiWidgetGroup from "../../../../components/data/KpiWidgetGroup";
import LoanTabShell from "../../shared/tabLayout/LoanTabShell";
import { UploadQueueProvider } from "../LoanDrilldownDocumentsTab/UploadQueueContext";
import { FixedUploadQueue } from "../LoanDrilldownDocumentsTab/LoanDrilldownDocumentsTab";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";

// Doc groups used for property documents — mirrors the legacy Properties tab
// (and `LoanSubjectPropertyFileUploadProcess.PROPERTY_DOC_GROUPS`).
const PROPERTY_DOC_GROUPS = [
  "Appraisal",
  "Construction Documents",
  "Insurance Docs",
  "Leases & Rents",
  "Property Documents",
  "Title Docs",
];

// Card density. "detailed" = the full card (map, files, associations, notes);
// "compact" = the dense grid card, which is what makes a 15-property loan
// scannable without endless scrolling.
const VIEW_DETAILED = "detailed";
const VIEW_COMPACT = "compact";
const VIEW_STORAGE_KEY = nsKey("loan-drilldown-properties-card-view");

// Loans with more properties than this land in compact by default — the point
// at which the detailed stack stops being scannable. An explicit choice is
// persisted and always wins over this.
const COMPACT_DEFAULT_THRESHOLD = 6;

const LoanDrilldownSubjectPropertiesTab = () => {
  const dispatch = useDispatch();

  const properties = useSelector(
    (state) => state?.loanDrilldown?.subjectProperties || [],
  );
  const loanDocuments = useSelector(
    (state) => state?.loanDrilldown?.loanDocuments || [],
  );
  const detailSidebar = useSelector((state) => state.detailSidebar);

  // Zillow market data answers for every zip on the loan — fetched once here
  // and handed to each card, which picks its own zip's row.
  const dealId = useSelector((state) => state?.loanDrilldown?._id);
  const market = useLoanMarketData(dealId);

  const selectedId =
    detailSidebar.contentType === DETAIL_SIDEBAR_CONTENT_TYPES.SUBJECT_PROPERTY
      ? detailSidebar.entityId
      : null;

  const rentalCount = useMemo(
    () =>
      size(
        properties?.filter((p) =>
          (p?.proposedPropertyType || p?.propertyType || "")
            .toLowerCase()
            .includes("rental"),
        ),
      ),
    [properties],
  );

  const aggregates = useMemo(() => {
    const sumNum = (key) =>
      properties.reduce((acc, p) => {
        const n = Number(p?.[key]);
        return Number.isFinite(n) ? acc + n : acc;
      }, 0);
    const totalAsIs = sumNum("asIsValue");
    const totalArv = sumNum("arv");
    const totalPurchase = sumNum("purchasePrice");
    const totalSqFt = sumNum("residentialSqFootage");
    const floodZoneCount = properties.filter((p) =>
      Boolean(p?.floodZone),
    ).length;
    // "Needs flood cert" = no cert on file. A manually entered `floodCertDate`
    // counts as satisfied (the cert was obtained outside the Xactus order
    // flow); otherwise fall back to the last order result, where missing,
    // errored or skipped all mean the property still needs one.
    const needsFloodCertCount = properties.filter((p) => {
      if (!isNil(p?.floodCertDate) && p.floodCertDate !== "") return false;
      const r = p?.lastFloodCertResult;
      if (!r) return true;
      return r.status === "error" || r.status === "skipped";
    }).length;
    return {
      totalAsIs,
      totalArv,
      totalPurchase,
      totalSqFt,
      floodZoneCount,
      needsFloodCertCount,
    };
  }, [properties]);

  // Files area — flat list of every loan doc whose docGroup is one of the
  // property doc groups; the grouped layout is applied inside the table.
  const propertyDocs = useMemo(
    () =>
      (loanDocuments || []).filter(
        (d) => d?.docGroup && PROPERTY_DOC_GROUPS.includes(d.docGroup),
      ),
    [loanDocuments],
  );

  // `null` = the user hasn't picked a density yet, so fall back to the
  // count-based default below. Once they pick, the choice sticks across visits
  // (partner-scoped so a Lendmarq build on the same origin keeps its own).
  const [storedView, setStoredView] = useLocalStorageState(VIEW_STORAGE_KEY, {
    defaultValue: null,
  });
  const view =
    storedView === VIEW_DETAILED || storedView === VIEW_COMPACT
      ? storedView
      : size(properties) > COMPACT_DEFAULT_THRESHOLD
        ? VIEW_COMPACT
        : VIEW_DETAILED;
  const isCompact = view === VIEW_COMPACT;

  // `unitsAction` ("edit" | "add") lands the sidebar on its Units tab — "add"
  // with a new unit row open. The unique key makes a repeat click re-apply it.
  const handleSelectProperty = useCallback(
    (property, unitsAction) => {
      dispatch(
        detailSidebarOpen(
          DETAIL_SIDEBAR_CONTENT_TYPES.SUBJECT_PROPERTY,
          property,
          property?._id,
          {
            title:
              addressLabel(property?.address) ||
              property?.address?.street ||
              "Property",
            mode: "view",
            source: "LoanDrilldownSubjectPropertiesTab",
            sourceField: "subjectProperties",
            ...(unitsAction
              ? {
                  unitsAction,
                  key: `SUBJECT_PROPERTY_${property?._id}_units_${Date.now()}`,
                }
              : {}),
          },
        ),
      );
    },
    [dispatch],
  );

  return (
    // Files dropped on any property card's documents rail stage here and are
    // reviewed / committed from the bottom-docked FixedUploadQueue, exactly
    // like the Applicants, Documents and Entity tabs.
    <UploadQueueProvider>
      <LoanTabShell
        title="Properties"
        subTitle="Properties featured this loan."
        primaryAction={{
          label: "Add property",
          icon: <AddBusinessOutlined />,
          onClick: () =>
            dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.ADD_PROPERTY)),
        }}
        bottomContent={
          size(properties) > 0 ? (
            <PropertiesSummaryBar
              properties={properties}
              rentalCount={rentalCount}
              aggregates={aggregates}
            />
          ) : undefined
        }
      >
        <Flx column>
          <Flx ac jb wrap gap={2} sx={{ mb: 1.5 }}>
            <Flx column>
              <Htag h3>All Loan Properties</Htag>
              <Txt secondary sx={{ fontSize: 12 }}>
                Click any property to view or edit details.
              </Txt>
            </Flx>
            {size(properties) > 0 && (
              <CardViewSwitcher view={view} onChange={setStoredView} />
            )}
          </Flx>
          {size(properties) === 0 ? (
            <Flx
              center
              column
              sx={{
                py: 8,
                color: grey[400],
                gap: 1,
              }}
            >
              <StoreOutlined sx={{ fontSize: 40 }} />
              <Box sx={{ fontSize: 14, fontWeight: 500 }}>
                No subject properties on this loan yet
              </Box>
            </Flx>
          ) : isCompact ? (
            <FlxGrid width="330px" gap={2}>
              {properties.map((property) => (
                <PropertyCardCompact
                  key={property._id}
                  property={property}
                  isSelected={selectedId === property._id}
                  onClick={() => handleSelectProperty(property)}
                />
              ))}
            </FlxGrid>
          ) : (
            <Flx column gap={2}>
              {properties.map((property) => (
                <PropertyCard
                  key={property._id}
                  property={property}
                  market={market}
                  isSelected={selectedId === property._id}
                  onClick={() => handleSelectProperty(property)}
                  onEditUnits={() => handleSelectProperty(property, "edit")}
                  onAddUnits={() => handleSelectProperty(property, "add")}
                />
              ))}
            </Flx>
          )}
        </Flx>

        {size(properties) > 0 && (
          <Box>
            <PropertyFilesArea
              propertyDocs={propertyDocs}
              totalCount={propertyDocs.length}
            />
          </Box>
        )}
      </LoanTabShell>
      <FixedUploadQueue />
    </UploadQueueProvider>
  );
};

/* ─────────────────────────────────────────────
   Card density switcher — Detailed ↔ Compact
   ───────────────────────────────────────────── */

const VIEW_OPTIONS = [
  {
    key: VIEW_DETAILED,
    label: "Detailed",
    icon: ViewAgendaOutlined,
    tooltip: "Full cards — map, documents, associations and notes",
  },
  {
    key: VIEW_COMPACT,
    label: "Compact",
    icon: GridViewOutlined,
    tooltip: "Dense grid — best for loans with many properties",
  },
];

const CardViewSwitcher = memo(({ view, onChange }) => (
  <Box
    sx={{
      display: "inline-flex",
      background: "#eef3f5",
      border: "1px solid #e3e9ec",
      borderRadius: "9px",
      p: "3px",
    }}
  >
    {VIEW_OPTIONS.map(({ key, label, icon: Icon, tooltip }) => {
      const on = view === key;
      return (
        <Tooltip key={key} title={tooltip} arrow>
          <Box
            component="button"
            type="button"
            onClick={() => onChange(key)}
            sx={{
              font: "inherit",
              fontSize: 12.5,
              fontWeight: on ? 600 : 500,
              color: on ? "#0f172a" : "#64748b",
              border: "none",
              background: on ? "#fff" : "transparent",
              boxShadow: on ? "0 1px 3px rgba(22,36,43,.12)" : "none",
              px: "12px",
              py: "6px",
              borderRadius: "7px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Icon sx={{ fontSize: 15 }} />
            {label}
          </Box>
        </Tooltip>
      );
    })}
  </Box>
));

/* ─────────────────────────────────────────────
   Summary widget — high-level aggregates
   ───────────────────────────────────────────── */

const PropertiesSummaryBar = memo(({ properties, rentalCount, aggregates }) => {
  const propertyCount = size(properties);
  // Total ARV is construction-only — hidden for DSCR.
  const construction = isConstructionProduct(
    useSelector((state) => state?.loanDrilldown?.loanProductType),
  );
  if (propertyCount === 0) return null;
  const items = [
    {
      key: "properties",
      label: "Properties",
      value: propertyCount,
      icon: <HomeWorkOutlined />,
    },
    ...(rentalCount > 0
      ? [
          {
            key: "rentals",
            label: "Rentals",
            value: rentalCount,
            icon: <StoreOutlined />,
          },
        ]
      : []),
    {
      key: "asIs",
      label: "Total As-Is",
      value: formatDollar(aggregates.totalAsIs, "—"),
    },
    ...(construction
      ? [
          {
            key: "arv",
            label: "Total ARV",
            value: formatDollar(aggregates.totalArv, "—"),
            tone: aggregates.totalArv > 0 ? "green" : "neutral",
          },
        ]
      : []),
    {
      key: "purchase",
      label: "Total Purchase",
      value: formatDollar(aggregates.totalPurchase, "—"),
    },
    {
      key: "sqft",
      label: "Res Sq Ft",
      value:
        aggregates.totalSqFt > 0 ? formatNumber(aggregates.totalSqFt) : "—",
    },
    ...(aggregates.floodZoneCount > 0
      ? [
          {
            key: "floodZone",
            label: "In flood zone",
            value: aggregates.floodZoneCount,
            tone: "blue",
            icon: <WaterOutlined />,
          },
        ]
      : []),
    ...(aggregates.needsFloodCertCount > 0
      ? [
          {
            key: "needsFloodCert",
            label: "Need flood cert",
            value: aggregates.needsFloodCertCount,
            tone: "red",
          },
        ]
      : []),
  ];

  return <KpiWidgetGroup items={items} sx={{ mb: "24px" }} />;
});

/* ─────────────────────────────────────────────
   Property Files area — per-doc-group tables
   ─────────────────────────────────────────────
   One collapsible section per doc group, each rendered with the shared
   LoanDocumentManagerTable. Empty groups are hidden by default to reduce
   noise but can be expanded via the "Show empty groups" toggle.
*/

const PropertyFilesArea = memo(({ propertyDocs, totalCount }) => (
  <Box sx={{ mt: 6 }}>
    <Flx ac jb sx={{ mb: 1.5 }}>
      <Flx column>
        <Htag h3>All Loan Property Related Documents</Htag>
        <Txt secondary sx={{ fontSize: 12 }}>
          Documents on this loan in any property doc group, grouped by category.
        </Txt>
      </Flx>
      <Flx ac g={0.5}>
        <FolderOpenOutlined sx={{ fontSize: 16, color: grey[500] }} />
        <Txt sx={{ fontSize: 12, color: grey[700], fontWeight: 600 }}>
          {totalCount} total
        </Txt>
      </Flx>
    </Flx>

    <Box
      sx={{
        background: "#fff",
        border: "1px solid #d8e0ea",
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow:
          "0 2px 4px rgba(15, 23, 42, 0.05), 0 6px 16px rgba(15, 23, 42, 0.07)",
      }}
    >
      {totalCount === 0 ? (
        <Flx center column sx={{ py: 6, color: grey[400], gap: 1 }}>
          <AttachFileOutlined sx={{ fontSize: 32 }} />
          <Box sx={{ fontSize: 13 }}>No property files yet</Box>
        </Flx>
      ) : (
        <LoanDocumentManagerTable
          rowData={propertyDocs}
          forcedLayout="docGroup"
          minHeight={"unset"}
          // This card clips overflow, so it is the toolbar's sticky context;
          // the nav-height offset would only open a blank band above it.
          stickyTop="0px"
        />
      )}
    </Box>
  </Box>
));

export default LoanDrilldownSubjectPropertiesTab;
